import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, LinearProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack,
} from '@mui/material';
import {
  BarChart as StatsIcon,
  Business as TenantsIcon,
  People as PeopleIcon,
  TrendingUp as ActiveIcon,
} from '@mui/icons-material';
import { platformApi } from '../api/services';

interface PlatformStats {
  totalTenants: number;
  activeTenants: number;
  totalUsers: number;
  activeThisMonth: number;
  tenantsByPlan: { plan: string; count: number }[];
  recentTenants: { id: string; name: string; plan: string; status: string; createdAt: string }[];
  currentMonthUsage: {
    planGenerations: number;
    transcriptsProcessed: number;
    aiTokensUsed: number;
  };
}

const COLORS = {
  primary: '#6C9BFF',
  secondary: '#FF8A65',
  success: '#69F0AE',
  error: '#FF5252',
  warning: '#FFB74D',
  bgPaper: '#121829',
  textPrimary: '#E8ECF4',
  textSecondary: '#8892A8',
  gridLine: '#1E293B',
};

const PLAN_COLORS: Record<string, string> = {
  Starter: COLORS.primary,
  Professional: COLORS.success,
  Enterprise: COLORS.warning,
  Custom: COLORS.secondary,
};

function StatCard({ title, value, icon, color }: { title: string; value: number | string; icon: React.ReactNode; color: string }) {
  return (
    <Card sx={{ bgcolor: COLORS.bgPaper, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2,
          bgcolor: `${color}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: color,
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h4" sx={{ color: COLORS.textPrimary, fontWeight: 700, lineHeight: 1.2 }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.textSecondary, fontSize: '0.8rem' }}>
            {title}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function PlatformOverviewPage() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await platformApi.getStats();
      setStats(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load platform stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <StatsIcon sx={{ color: 'error.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h4">Platform Overview</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            System-wide metrics and tenant activity
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress color="error" sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {stats && (
        <Stack spacing={2.5}>
          {/* Stats Cards */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Total Tenants"
                value={stats.totalTenants}
                icon={<TenantsIcon />}
                color={COLORS.primary}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Active Tenants"
                value={stats.activeTenants}
                icon={<ActiveIcon />}
                color={COLORS.success}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Total Users"
                value={stats.totalUsers}
                icon={<PeopleIcon />}
                color={COLORS.secondary}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Active This Month"
                value={stats.activeThisMonth}
                icon={<PeopleIcon />}
                color={COLORS.warning}
              />
            </Grid>
          </Grid>

          {/* Tenants by Plan + Current Month Usage */}
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ bgcolor: COLORS.bgPaper, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: COLORS.textPrimary, fontWeight: 600 }}>
                    Tenants by Plan
                  </Typography>
                  {stats.tenantsByPlan.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Plan</TableCell>
                            <TableCell align="right" sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Tenants</TableCell>
                            <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600, width: '40%' }}>Distribution</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stats.tenantsByPlan.map(row => {
                            const total = stats.tenantsByPlan.reduce((s, r) => s + r.count, 0);
                            const pct = total > 0 ? (row.count / total) * 100 : 0;
                            const color = PLAN_COLORS[row.plan] || COLORS.primary;
                            return (
                              <TableRow key={row.plan}>
                                <TableCell>
                                  <Chip
                                    label={row.plan}
                                    size="small"
                                    sx={{ bgcolor: `${color}20`, color: color, fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ color: COLORS.textPrimary, fontWeight: 600 }}>
                                    {row.count}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={pct}
                                      sx={{
                                        flex: 1, height: 6, borderRadius: 3,
                                        bgcolor: 'rgba(255,255,255,0.05)',
                                        '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
                                      }}
                                    />
                                    <Typography variant="caption" sx={{ color: COLORS.textSecondary, minWidth: 32, textAlign: 'right' }}>
                                      {pct.toFixed(0)}%
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No tenant data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ bgcolor: COLORS.bgPaper, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: COLORS.textPrimary, fontWeight: 600 }}>
                    Current Month Usage
                  </Typography>
                  <Stack spacing={2.5}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>Plan Generations</Typography>
                        <Typography variant="body2" sx={{ color: COLORS.primary, fontWeight: 600 }}>
                          {stats.currentMonthUsage.planGenerations.toLocaleString()}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, stats.currentMonthUsage.planGenerations / 10)}
                        sx={{
                          height: 6, borderRadius: 3,
                          bgcolor: 'rgba(255,255,255,0.05)',
                          '& .MuiLinearProgress-bar': { bgcolor: COLORS.primary, borderRadius: 3 },
                        }}
                      />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>Transcripts Processed</Typography>
                        <Typography variant="body2" sx={{ color: COLORS.success, fontWeight: 600 }}>
                          {stats.currentMonthUsage.transcriptsProcessed.toLocaleString()}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, stats.currentMonthUsage.transcriptsProcessed / 5)}
                        sx={{
                          height: 6, borderRadius: 3,
                          bgcolor: 'rgba(255,255,255,0.05)',
                          '& .MuiLinearProgress-bar': { bgcolor: COLORS.success, borderRadius: 3 },
                        }}
                      />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>AI Tokens Used</Typography>
                        <Typography variant="body2" sx={{ color: COLORS.warning, fontWeight: 600 }}>
                          {stats.currentMonthUsage.aiTokensUsed.toLocaleString()}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, stats.currentMonthUsage.aiTokensUsed / 10000)}
                        sx={{
                          height: 6, borderRadius: 3,
                          bgcolor: 'rgba(255,255,255,0.05)',
                          '& .MuiLinearProgress-bar': { bgcolor: COLORS.warning, borderRadius: 3 },
                        }}
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recent Tenants */}
          <Card sx={{ bgcolor: COLORS.bgPaper }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: COLORS.textPrimary, fontWeight: 600 }}>
                Recent Tenants
              </Typography>
              {stats.recentTenants.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Plan</TableCell>
                        <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ color: COLORS.textSecondary, borderColor: COLORS.gridLine, fontWeight: 600 }}>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.recentTenants.map(tenant => (
                        <TableRow key={tenant.id} hover>
                          <TableCell sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>{tenant.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={tenant.plan}
                              size="small"
                              sx={{
                                bgcolor: `${PLAN_COLORS[tenant.plan] || COLORS.primary}20`,
                                color: PLAN_COLORS[tenant.plan] || COLORS.primary,
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tenant.status}
                              size="small"
                              color={tenant.status === 'Active' ? 'success' : tenant.status === 'Suspended' ? 'error' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
                              {new Date(tenant.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No tenants yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Stack>
      )}

      {!loading && !stats && !error && (
        <Card sx={{ bgcolor: COLORS.bgPaper }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">No platform data available</Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
